/**
 * @file       Settings signature pad (draw or upload)
 * @desc       Capture a PNG signature for the logged-in user and emit
 *             it for Settings save/clear against the user signature API.
 * @version    2.0.0
 * @copyright  Copyright (c) 2018-2026, MindShare HDV LLC.  All rights
 *             reserved.
 */

import
{
    Component,
    ViewChild,
    ElementRef,
    Input,
    Output,
    EventEmitter,
    AfterViewInit,
    OnChanges,
    SimpleChanges
} from '@angular/core';
import SignaturePad from 'signature_pad';

@Component
({
    selector: 'signature-pad',
    templateUrl: './signaturePad.component.html',
    styleUrls: ['./signaturePad.component.scss'],
    standalone: false
})
/**
 * @desc   - Draw or upload a signature image for user Settings
 */
class SignaturePadComponent implements AfterViewInit, OnChanges
{
    @ViewChild('canvas') signaturePadEl: ElementRef;

    @Input() savedSignatureUrl: string = '';

    @Output() signatureSaved = new EventEmitter<string>();
    @Output() signatureCleared = new EventEmitter<void>();

    signatureNeeded: boolean;
    signaturePad: SignaturePad | null;
    signatureImg: string;
    uploadError: string;

    /**
     * @desc   - component constructor
     * @access - public
     * @param  -
     * @return -
     */
    constructor()
    {
        this.signatureNeeded = false;
        this.signaturePad = null;
        this.signatureImg = '';
        this.uploadError = '';
    }

    /**
     * @desc   - Init pad after canvas is available
     * @access - public
     * @param  -
     * @return -
     */
    ngAfterViewInit()
    {
        this.signaturePad = new SignaturePad(
            this.signaturePadEl.nativeElement
        );
        this.applySavedUrlPreview();
    }

    /**
     * @desc   - Refresh preview when parent loads a saved URL
     * @access - public
     * @param  - changes : Angular input changes
     * @return -
     */
    ngOnChanges
    (
        changes: SimpleChanges
    )
    {
        if (changes.savedSignatureUrl)
        {
            this.applySavedUrlPreview();
        }
    }

    /**
     * @desc   - Touch start hook for device drawing
     * @access - public
     * @param  - event : touch event
     * @return -
     */
    startDrawing
    (
        event: Event
    )
    {
        // works in device not in browser
    }

    /**
     * @desc   - Touch move hook for device drawing
     * @access - public
     * @param  - event : touch event
     * @return -
     */
    moved
    (
        event: Event
    )
    {
        // works in device not in browser
    }

    /**
     * @desc   - Emit drawn signature as PNG data URL
     * @access - public
     * @param  -
     * @return -
     */
    savePad()
    {
        let base64Data;
        let empty;

        empty = !this.signaturePad || this.signaturePad.isEmpty();
        this.uploadError = '';

        if (empty)
        {
            this.uploadError = 'Draw a signature before saving.';
        }
        else
        {
            base64Data = this.signaturePad.toDataURL('image/png');
            this.signatureImg = base64Data;
            this.signatureSaved.emit(base64Data);
        }
    }

    /**
     * @desc   - Undo last stroke on the pad
     * @access - public
     * @param  -
     * @return -
     */
    undoSign()
    {
        let data;

        data = this.signaturePad ? this.signaturePad.toData() : null;

        if (data)
        {
            data.pop();
            this.signaturePad.fromData(data);
        }
    }

    /**
     * @desc   - Clear pad and notify parent to delete saved signature
     * @access - public
     * @param  -
     * @return -
     */
    clearPad()
    {
        this.uploadError = '';

        if (this.signaturePad)
        {
            this.signaturePad.clear();
        }

        this.signatureImg = '';
        this.signatureCleared.emit();
    }

    /**
     * @desc   - Load an uploaded image file into the pad preview / emit
     * @access - public
     * @param  - event : file input change event
     * @return -
     */
    onFileSelected
    (
        event: Event
    )
    {
        let input;
        let file;
        let reader;
        let fileType;

        this.uploadError = '';
        input = event.target as HTMLInputElement;
        file = input && input.files && input.files.length ?
            input.files[0] : null;
        fileType = file ? String(file.type || '') : '';

        if (!file)
        {
            this.uploadError = 'No image file selected.';
        }
        else if (fileType.indexOf('image/') !== 0)
        {
            this.uploadError = 'Please choose an image file.';
        }
        else
        {
            reader = new FileReader();
            reader.onload = () =>
            {
                let dataUrl;

                dataUrl = typeof reader.result === 'string' ?
                    reader.result : '';

                if (dataUrl)
                {
                    this.signatureImg = dataUrl;
                    this.signatureSaved.emit(dataUrl);
                }
                else
                {
                    this.uploadError = 'Unable to read image file.';
                }
            };
            reader.onerror = () =>
            {
                this.uploadError = 'Unable to read image file.';
            };
            reader.readAsDataURL(file);
        }

        if (input)
        {
            input.value = '';
        }
    }

    /**
     * @desc   - Show saved server URL in the preview area
     * @access - private
     * @param  -
     * @return -
     */
    private applySavedUrlPreview()
    {
        let url;

        url = this.savedSignatureUrl ?
            String(this.savedSignatureUrl) : '';

        if (url)
        {
            this.signatureImg = url;
        }
        else if (this.signatureImg &&
            this.signatureImg.indexOf('data:') !== 0)
        {
            this.signatureImg = '';
        }
    }
}
export { SignaturePadComponent }
